/**
 * Per-project timeline of agent turns.
 *
 * One row per `snapshots` table entry. The row shows:
 *   • short SHA + parent-SHA arrow (so the chain is visible)
 *   • turn summary (== git commit message, agent-generated)
 *   • files changed, cost, duration, turn count
 *   • relative timestamp
 *
 * The list is read-only in the MVP. Wk6 adds:
 *   • click-to-diff (Monaco diff editor against parent SHA)
 *   • click-to-rollback (creates a new branch at the prior turn,
 *     hard-resets main, refreshes the editor)
 *
 * Data source: GET /v1/projects/:id/snapshots — capped at 200 rows server-side.
 *
 * Refresh contract: caller passes `refreshKey` (incremented after a build
 * completes); the effect re-runs on key change and pulls fresh rows.
 */
import { useEffect, useState } from 'react'
import { History } from 'lucide-react'
import { api, type ApiError, type SnapshotDto } from '../lib/api'
import { Badge, StatusBox } from './ui/badge'
import { EmptyState } from './ui/empty-state'

type Props = {
  projectId: string
  accessToken: string
  refreshKey: number
}

export function Timeline({ projectId, accessToken, refreshKey }: Props) {
  const [snapshots, setSnapshots] = useState<SnapshotDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .listSnapshots(accessToken, projectId)
      .then((res) => {
        if (!cancelled) {
          setSnapshots(res.snapshots)
          setError(null)
        }
      })
      .catch((err: ApiError) => {
        if (!cancelled) setError(err.title ?? 'Failed to load timeline')
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, projectId, refreshKey])

  if (error) {
    return (
      <div className="p-4">
        <StatusBox tone="destructive">{error}</StatusBox>
      </div>
    )
  }
  if (snapshots === null) {
    return <p className="p-5 text-sm text-muted-foreground">Loading timeline…</p>
  }
  if (snapshots.length === 0) {
    return (
      <div className="p-4">
        <EmptyState
          icon={<History />}
          title="No snapshots yet"
          body="Each agent turn creates a checkpoint here. Send a prompt in the Chat tab to record the first one."
        />
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="text-sm font-semibold">Timeline</h3>
        <Badge variant="secondary" className="tabular-nums">
          {snapshots.length} {snapshots.length === 1 ? 'snapshot' : 'snapshots'}
        </Badge>
      </header>
      <ol className="min-h-0 flex-1 divide-y overflow-y-auto">
        {snapshots.map((snap) => (
          <li key={snap.id} className="px-4 py-3 transition-colors hover:bg-muted/50">
            <div className="flex items-baseline justify-between gap-3">
              <div className="flex items-baseline gap-2 font-mono text-xs text-muted-foreground">
                <span title={snap.gitSha}>{snap.gitSha.slice(0, 7)}</span>
                {snap.parentSha && (
                  <>
                    <span className="text-muted-foreground/50">←</span>
                    <span title={snap.parentSha}>{snap.parentSha.slice(0, 7)}</span>
                  </>
                )}
              </div>
              <time className="shrink-0 text-xs text-muted-foreground" dateTime={snap.createdAt}>
                {formatRelative(snap.createdAt)}
              </time>
            </div>
            <p className="mt-1 line-clamp-2 text-sm text-foreground">
              {snap.summary ?? <span className="italic text-muted-foreground">(no summary)</span>}
            </p>
            <dl className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground tabular-nums">
              <span>
                {snap.filesChanged} {snap.filesChanged === 1 ? 'file' : 'files'}
              </span>
              {snap.numTurns !== null && (
                <span>
                  {snap.numTurns} {snap.numTurns === 1 ? 'turn' : 'turns'}
                </span>
              )}
              {snap.durationMs !== null && <span>{(snap.durationMs / 1000).toFixed(1)}s</span>}
              {snap.costUsd && <span>${Number(snap.costUsd).toFixed(4)}</span>}
            </dl>
          </li>
        ))}
      </ol>
    </div>
  )
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return 'just now'
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`
  if (ms < 604_800_000) return `${Math.floor(ms / 86_400_000)}d ago`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
